
import BCXInfoSection from '@/components/molecules/BCXInfoSection.vue';
import InvitesIcon from '@/components/svg/invites-icon.svg?inline';
import {
  computed, defineComponent, onBeforeUnmount, onMounted, provide, reactive, watch
} from 'vue';
import { getInviteCodes, updateInviteCode } from '@/services/Invitation/Api';
import BCXMarkdown from '@/components/molecules/BCXMarkdown.vue';
import { InviteCode, InviteCodesResponse } from '@/models/Invitation';
import sortComp from '@/utils/sortComp';
import { watchDebounced } from '@vueuse/core';
import InvitesCodesTable from '@/views/invites/InvitesCodesTable.vue';
import useUser from '@/mixins/useUser';

export default defineComponent({
  name: 'InvitesCodes',
  components: {
    InvitesCodesTable,
    BCXMarkdown,
    BCXInfoSection,
    InvitesIcon,
  },
  setup() {
    const invitesResponse = reactive<InviteCodesResponse>({ inviteCodes: [] });
    const { tUserFallback: t } = useUser('self');

    const loadInvites = async () => {
      const inviteCodes = (await getInviteCodes())?.inviteCodes ?? [];
      inviteCodes.sort((a: InviteCode, b: InviteCode) => sortComp(b.userMarkedAsUsed, a.userMarkedAsUsed) * 2 + sortComp(a.userMarkedAsUsedTs, b.userMarkedAsUsedTs));
      invitesResponse.inviteCodes = inviteCodes;
    };

    const inviteCodes = computed<InviteCode[]>(() => invitesResponse.inviteCodes.filter((code) => !code.affiliate));

    onMounted(loadInvites);

    provide('invites', inviteCodes);

    const userMarked = computed<string[]>(() => inviteCodes.value.map((code) => `${code.userMarkedAsUsed} ${code.userNote}`));

    let firstPrev: string[] | null = null;
    let prev: string[] = [];

    watch(userMarked, (userMarked) => {
      if (!prev.length && userMarked?.length) prev = userMarked;
    });

    const updateInviteCodes = async (cur: string[], prev: string[]) => {
      const promises = [];
      let i;
      for (i = 0; i < cur.length; i++) {
        if (prev[i] !== cur[i]) {
          const curInviteCode = inviteCodes.value[i];
          const { userNote, inviteCode, userMarkedAsUsed } = curInviteCode;

          promises.push(
            updateInviteCode(inviteCode, {
              userNote: !userMarkedAsUsed ? '' : userNote ?? '',
              userMarkedAsUsed
            })
          );
        }
      }
      if (promises.length) {
        const results = await Promise.all(promises);
        results.forEach((resultInviteCode) => {
          const inviteCodeIndex = invitesResponse.inviteCodes.findIndex(
            (inviteCode: InviteCode) => inviteCode.inviteCode === resultInviteCode.inviteCode
          );
          if (inviteCodeIndex >= 0) {
            invitesResponse.inviteCodes.splice(inviteCodeIndex, 1, resultInviteCode);
          }
        });
      }
      firstPrev = null;
    };

    watchDebounced(userMarked, async (cur) => {
      firstPrev ??= prev;
      if (prev.length) await updateInviteCodes(cur, prev);
      prev = [...cur];
    }, {
      debounce: 1000
    });

    onBeforeUnmount(() => {
      if (firstPrev) {
        updateInviteCodes(firstPrev, userMarked.value);
      }
    });

    return {
      t
    };
  }
});
